import React from "react";
import Table from "../../../components/table/table";
import Icon from "../../../components/icon/icon";
import { VideoReview } from "../../../types";
import { VideoReviewTableProps } from "../types/video-reviews-types";
import { ITEMS_PER_PAGE_OPTIONS } from "../../../constants/pagination";
import {
  getVideoReviewDisplayName,
  getVideoReviewThumbnail,
  getVideoReviewVideoUrl,
  getVideoReviewContent,
  getVideoReviewRole,
  getVideoReviewDate,
} from "../utils/video-reviews-utils";

export const VideoReviewTable: React.FC<VideoReviewTableProps> = ({
  reviews,
  isLoading,
  currentPage,
  itemsPerPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const columns = [
    {
      title: "Превью",
      dataIndex: "image" as keyof VideoReview,
      key: "thumbnail",
      render: (url: string, record: VideoReview) => {
        const imageUrl = getVideoReviewThumbnail(record);
        return imageUrl ? (
          <img
            src={imageUrl}
            alt="Video review thumbnail"
            width="40"
            height="40"
            loading="lazy"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              objectFit: "cover",
              backgroundColor: "#f0f0f0",
            }}
            className="video-review__thumbnail"
          />
        ) : (
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              color: "#999",
            }}
          >
            Нет превью
          </div>
        );
      },
    },
    {
      title: "Имя",
      dataIndex: "name" as keyof VideoReview,
      key: "name",
      render: (name: string, record: VideoReview) =>
        getVideoReviewDisplayName(record),
    },
    {
      title: "Роль",
      dataIndex: "role" as keyof VideoReview,
      key: "role",
      render: (role: string, record: VideoReview) =>
        getVideoReviewRole(record) || "",
    },
    {
      title: "Дата",
      dataIndex: "date" as keyof VideoReview,
      key: "date",
      render: (date: string, record: VideoReview) => {
        const dateValue = getVideoReviewDate(record);
        return dateValue ? new Date(dateValue).toLocaleDateString("ru-RU") : "";
      },
    },
    {
      title: "Описание",
      dataIndex: "description" as keyof VideoReview,
      key: "description",
      render: (text: string, record: VideoReview) => {
        const description = getVideoReviewContent(record);
        if (!description) return "";
        const maxLength = 100;
        return description.length > maxLength
          ? `${description.substring(0, maxLength)}...`
          : description;
      },
    },
    {
      title: "Видео",
      dataIndex: "video_link" as keyof VideoReview,
      key: "video",
      render: (url: string, record: VideoReview) => {
        const videoUrl = getVideoReviewVideoUrl(record);
        return videoUrl ? (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="video-review__video-link"
          >
            Смотреть видео
          </a>
        ) : (
          <span className="video-review__no-video">Нет видео</span>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      dataIndex: "id" as keyof VideoReview,
      render: (_: any, record: VideoReview) => (
        <div className="video-review__actions">
          <button
            onClick={() => onEdit(record)}
            className="video-review__action-btn"
            title="Редактировать"
          >
            <Icon name="pencil" size="small" />
          </button>
          <button
            onClick={() => onDelete(record)}
            className="video-review__action-btn"
            title="Удалить"
          >
            <Icon name="delete" size="small" />
          </button>
        </div>
      ),
    },
  ];

  const handleItemsPerPageChange = (value: string): void => {
    onItemsPerPageChange(parseInt(value));
  };

  return (
    <div className="video-review__table-container">
      <Table
        columns={columns}
        dataSource={reviews}
        rowKey="id"
        loading={isLoading}
        emptyMessage="Видео-отзывы не найдены"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />

      <div className="video-review__pagination">
        <div className="video-review__pagination-info">
          Показано{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, reviews.length)} -{" "}
          {Math.min(currentPage * itemsPerPage, reviews.length)} из{" "}
          {reviews.length}
        </div>

        <div className="video-review__pagination-controls">
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
            className="video-review__page-size-select"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} на странице
              </option>
            ))}
          </select>

          <div className="video-review__page-buttons">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="video-review__page-btn"
            >
              <Icon name="left" size="small" />
            </button>

            <span className="video-review__page-info">
              {currentPage} из {totalPages}
            </span>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="video-review__page-btn"
            >
              <Icon name="right" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
